import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LifeBuoy, Clock, Users2, MessageSquareText, Plus, Archive, Save } from "lucide-react";

const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const TZONES = ["America/Chicago", "America/New_York", "America/Denver", "America/Los_Angeles", "America/Phoenix", "UTC"];
const DOT = { available: "bg-emerald-500", away: "bg-amber-500", offline: "bg-slate-400" };

export default function SupportSettings() {
  const [tab, setTab] = useState("general");
  const [s, setS] = useState(null);
  const [meta, setMeta] = useState(null);
  const [orgId, setOrgId] = useState(() => localStorage.getItem("tr_district") || "");
  const [canEdit, setCanEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  const apply = (d) => {
    setMeta(d); setS(d.settings); setCanEdit(d.can_edit);
    if (d.scope === "account" && !d.can_edit_account) {
      const own = d.organizations.find((o) => o.can_edit);
      if (own) setOrgId(own.id);
    }
  };
  const load = useCallback(() => {
    api.get("/support/settings", { params: { organization_id: orgId || undefined } }).then((r) => apply(r.data))
      .catch((e) => { if (orgId && e?.response?.status === 403) setOrgId(""); else toast.error(apiError(e)); });
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/support/settings", { ...s, organization_id: orgId || null });
      apply(data); toast.success(orgId ? `Saved for ${data.organization?.name}` : "Account defaults saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset all of this group's overrides to the account defaults?")) return;
    try { const { data } = await api.post("/support/settings/reset", { organization_id: orgId }); apply(data); toast.success("Group now uses account defaults"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const setDay = (d, field, v) => setS((x) => ({ ...x, support_hours: { ...x.support_hours, [d]: { ...(x.support_hours?.[d] || { open: "08:00", close: "17:00" }), [field]: v } } }));
  const toggleDay = (d, closed) => setS((x) => ({ ...x, support_hours: { ...x.support_hours, [d]: closed ? null : { open: "08:00", close: "17:00" } } }));

  if (!s || !meta) return <div className="h-64 animate-pulse rounded-xl bg-white" />;
  const TABS = [["general", "General", LifeBuoy], ["hours", "Support Hours", Clock], ["team", "Team", Users2], ["quick", "Quick Replies", MessageSquareText]];
  const ov = new Set(meta.overridden || []);
  const tag = (k) => meta.scope === "group" ? <span className={`ml-1 text-[10px] rounded px-1.5 py-0.5 ${ov.has(k) ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"}`} data-testid={`inherit-${k}`}>{ov.has(k) ? "Group override" : "Inherited"}</span> : null;
  const groupName = meta.organization?.name;
  const explain = meta.scope === "group"
    ? (canEdit ? `You can change Support for ${groupName}. Values marked Inherited follow the account defaults until you change them.` : `You can view ${groupName}'s Support settings. Only this group's admins or an Account Admin can change them.`)
    : (canEdit ? "You're editing the account defaults. Every group inherits these unless it overrides them." : "You're viewing the account defaults (Account Admins edit these). Choose your group above to manage its Support settings.");

  return (
    <div data-testid="support-settings">
      <PageHeader title="Support Settings" description="Configure the human Support Chat experience for your account and groups." />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={orgId || "account"} onValueChange={(v) => setOrgId(v === "account" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-[300px] h-10 font-semibold" data-testid="support-settings-scope"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="account">Account defaults (all groups)</SelectItem>
            {meta.organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}{o.can_edit ? "" : " (view only)"}</SelectItem>)}
          </SelectContent>
        </Select>
        {meta.scope === "group" && canEdit && ov.size > 0 && <Button size="sm" variant="outline" onClick={reset} data-testid="support-settings-reset">Reset to account defaults</Button>}
      </div>
      <div className={`mb-4 rounded-lg border px-3 py-2 text-xs ${canEdit ? "bg-sky-50 border-sky-200 text-sky-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="support-settings-scope-note">{explain}</div>
      {meta.setup?.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-0.5" data-testid="support-settings-setup">
          {meta.setup.map((m) => <p key={m}>{m}</p>)}
        </div>
      )}
      <div className="flex gap-1 border-b border-slate-200 mb-5 overflow-x-auto tr-scroll">
        {TABS.map(([k, l, I]) => (
          <button key={k} data-testid={`support-settings-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === k ? "border-brand text-brand" : "border-transparent text-slate-500"}`}><I className="w-4 h-4" />{l}</button>
        ))}
      </div>

      {tab === "general" && (
        <div className="max-w-2xl space-y-4">
          <Panel>
            <Toggle label={<>{meta.scope === "group" ? `Support enabled for ${groupName}` : "Support enabled for this Client Account"}{tag("support_enabled")}</>} checked={s.support_enabled} onChange={(v) => set("support_enabled", v)} disabled={!canEdit || (meta.scope === "group" && !s.account_support_enabled)} testId="set-support-enabled" />
            <Toggle label="Candidate Support enabled" checked={s.candidate_support_enabled} onChange={(v) => set("candidate_support_enabled", v)} disabled={!canEdit} testId="set-candidate-support-enabled" />
            <Toggle label="Send offline acknowledgement email" checked={s.offline_email_enabled} onChange={(v) => set("offline_email_enabled", v)} disabled={!canEdit} testId="set-offline-email" />
            <Toggle label="Proactive Support invitations (staff can greet online candidates)" checked={s.proactive_invitations_enabled} onChange={(v) => set("proactive_invitations_enabled", v)} disabled={!canEdit} testId="set-proactive" />
          </Panel>
          <Panel>
            <div><Label className="text-xs font-semibold">Offline message</Label>
              <Textarea rows={2} value={s.offline_message || ""} onChange={(e) => set("offline_message", e.target.value)} disabled={!canEdit} data-testid="set-offline-message" /></div>
            <div><Label className="text-xs font-semibold">Configured reply-time text <span className="font-normal text-slate-400">(optional — only shown if set)</span></Label>
              <Input value={s.reply_time_text || ""} onChange={(e) => set("reply_time_text", e.target.value)} disabled={!canEdit} placeholder="e.g. Messages are typically answered within one business day." data-testid="set-reply-time" /></div>
          </Panel>
          {canEdit && <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="support-settings-save"><Save className="w-4 h-4 mr-1.5" />{saving ? "Saving…" : "Save Settings"}</Button>}
        </div>
      )}

      {tab === "hours" && (
        <div className="max-w-2xl space-y-4">
          <Panel>
            <div><Label className="text-xs font-semibold">Timezone{tag("timezone")}</Label>
              <Select value={s.timezone} onValueChange={(v) => set("timezone", v)} disabled={!canEdit}>
                <SelectTrigger data-testid="set-timezone" className="max-w-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{TZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <p className="text-xs text-slate-400">Hours are local to the timezone above.{tag("support_hours")} Support hours do not make anyone appear Online by themselves — a candidate sees Online only inside these hours with an available, active support user.</p>
            <div className="divide-y divide-slate-100">
              {DAYS.map(([d, label]) => {
                const h = s.support_hours?.[d];
                const closed = !h;
                return (
                  <div key={d} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2" data-testid={`hours-row-${d}`}>
                    <span className="w-24 text-sm text-slate-700">{label}</span>
                    <label className="flex items-center gap-1.5 text-xs text-slate-500"><input type="checkbox" checked={!closed} disabled={!canEdit} onChange={(e) => toggleDay(d, !e.target.checked)} data-testid={`hours-open-${d}`} />Open</label>
                    {!closed ? (
                      <div className="flex items-center gap-2">
                        <input type="time" value={h.open} disabled={!canEdit} onChange={(e) => setDay(d, "open", e.target.value)} className="h-8 rounded border border-slate-200 px-2 text-sm" data-testid={`hours-from-${d}`} />
                        <span className="text-slate-400 text-xs">to</span>
                        <input type="time" value={h.close} disabled={!canEdit} onChange={(e) => setDay(d, "close", e.target.value)} className="h-8 rounded border border-slate-200 px-2 text-sm" data-testid={`hours-to-${d}`} />
                      </div>
                    ) : <span className="text-sm text-slate-400">Closed</span>}
                  </div>
                );
              })}
            </div>
          </Panel>
          {canEdit && <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="support-hours-save"><Save className="w-4 h-4 mr-1.5" />{saving ? "Saving…" : "Save Hours"}</Button>}
        </div>
      )}

      {tab === "team" && <TeamTab orgId={orgId} onChanged={load} />}
      {tab === "quick" && <QuickRepliesTab orgId={orgId} />}
    </div>
  );
}

const Panel = ({ children }) => <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3 shadow-sm">{children}</div>;
const Toggle = ({ label, checked, onChange, disabled, testId }) => (
  <label className="flex items-center justify-between gap-3 text-sm text-slate-700 py-1">
    <span>{label}</span>
    <button type="button" disabled={disabled} onClick={() => onChange(!checked)} data-testid={testId}
      className={`relative w-10 h-5.5 rounded-full transition-colors ${checked ? "bg-brand" : "bg-slate-300"} ${disabled ? "opacity-50" : ""}`} style={{ height: 22, width: 40 }}>
      <span className={`absolute top-0.5 w-[18px] h-[18px] rounded-full bg-white transition-all ${checked ? "left-[19px]" : "left-0.5"}`} />
    </button>
  </label>
);

function TeamTab({ orgId, onChanged }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/support/team").then((r) => setRows((r.data.team || []).filter((m) => !orgId || m.organization_id === orgId))).catch((e) => { setRows([]); toast.error(apiError(e)); }), [orgId]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (m, extra = {}) => {
    try { await api.patch("/support/agents", { user_id: m.user_id, organization_id: m.organization_id, can_handle_support_chat: !m.can_handle_support_chat, ...extra });
      toast.success("Support team updated"); load(); onChanged?.(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "open_conversations") {
        const others = rows.filter((r) => r.can_handle_support_chat && r.user_id !== m.user_id && r.organization_id === m.organization_id);
        const pick = others.length ? window.prompt(`${d.message}\n\nType the number of a member to reassign to, or leave blank to return them to the queue:\n${others.map((o, i) => `${i + 1}. ${o.user_name}`).join("\n")}`) : "";
        if (pick === null) return;
        const target = others[Number(pick) - 1];
        return toggle(m, target ? { open_conversations: "reassign", reassign_to: target.user_id } : { open_conversations: "unassign" });
      }
      toast.error(apiError(e));
    }
  };
  if (!rows) return <div className="h-40 animate-pulse rounded-xl bg-white" />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto" data-testid="support-team">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs text-slate-500"><tr>
          <th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Role</th><th className="px-4 py-2.5 hidden sm:table-cell">District</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5 text-right">Support</th>
        </tr></thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((m, i) => (
            <tr key={m.user_id + m.organization_id + i} data-testid={`team-row-${m.user_id}`}>
              <td className="px-4 py-2.5 font-medium text-slate-800">{m.user_name}</td>
              <td className="px-4 py-2.5 text-slate-500 capitalize">{(m.role || "").replace(/_/g, " ")}</td>
              <td className="px-4 py-2.5 text-slate-500 hidden sm:table-cell">{m.organization_name || "—"}</td>
              <td className="px-4 py-2.5"><span className="inline-flex items-center gap-1.5 text-xs text-slate-500"><span className={`w-2 h-2 rounded-full ${DOT[m.online ? "available" : m.status] || DOT.offline}`} />{m.online ? "Online" : (m.status || "offline")}</span></td>
              <td className="px-4 py-2.5 text-right">
                <button disabled={!m.can_manage} onClick={() => toggle(m)} data-testid={`team-toggle-${m.user_id}`}
                  className={`text-xs rounded-full px-2.5 py-1 ${m.can_handle_support_chat ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"} ${m.can_manage ? "hover:opacity-80" : "opacity-60 cursor-not-allowed"}`}>
                  {m.can_handle_support_chat ? "Enabled" : "Disabled"}
                </button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400 text-sm">No eligible staff in your scope.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function QuickRepliesTab({ orgId }) {
  const [rows, setRows] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [canGroup, setCanGroup] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [draft, setDraft] = useState(null);
  const load = useCallback(() => api.get("/support/quick-replies", { params: { include_archived: showArchived, organization_id: orgId || undefined } }).then((r) => { setRows(r.data.quick_replies || []); setCanManage(r.data.can_manage_shared); setCanGroup(r.data.can_manage_group); }).catch(() => setRows([])), [showArchived, orgId]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!draft?.title?.trim() || !draft?.body?.trim()) { toast.error("Title and body required"); return; }
    try { await api.post("/support/quick-replies", { ...draft, organization_id: orgId || null }); toast.success("Quick Reply added"); setDraft(null); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const patch = async (id, body) => { try { await api.patch(`/support/quick-replies/${id}`, body); load(); } catch (e) { toast.error(apiError(e)); } };
  if (!rows) return <div className="h-40 animate-pulse rounded-xl bg-white" />;
  return (
    <div className="max-w-2xl space-y-3" data-testid="support-quick-replies">
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs text-slate-500 ml-auto"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="qr-show-archived" />Show archived</label>
        <Button size="sm" variant="outline" onClick={() => setDraft({ scope: canGroup ? "group" : canManage ? "shared" : "personal", title: "", body: "" })} data-testid="qr-add"><Plus className="w-4 h-4 mr-1" />New Quick Reply</Button>
      </div>
      {draft && (
        <Panel>
          <div className="grid sm:grid-cols-2 gap-2">
            <div><Label className="text-xs font-semibold">Title</Label><Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="qr-title" /></div>
            <div><Label className="text-xs font-semibold">Scope</Label>
              <Select value={draft.scope} onValueChange={(v) => setDraft({ ...draft, scope: v })}>
                <SelectTrigger data-testid="qr-scope"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{canGroup && <SelectItem value="group">This group</SelectItem>}{canManage && <SelectItem value="shared">Shared (whole account)</SelectItem>}<SelectItem value="personal">Personal</SelectItem></SelectContent>
              </Select></div>
          </div>
          <div><Label className="text-xs font-semibold">Message</Label><Textarea rows={2} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="qr-body" /></div>
          <div className="flex gap-2 justify-end"><Button variant="outline" size="sm" onClick={() => setDraft(null)}>Cancel</Button><Button size="sm" className="bg-brand hover:bg-brand-strong" onClick={create} data-testid="qr-save">Save</Button></div>
        </Panel>
      )}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm divide-y divide-slate-50">
        {rows.map((q) => (
          <div key={q.id} className="px-4 py-3 flex items-start gap-3" data-testid={`qr-${q.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-800">{q.title} <span className={`ml-1 text-[10px] rounded-full px-1.5 py-0.5 ${q.scope === "shared" ? "bg-indigo-100 text-indigo-700" : q.scope === "group" ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"}`}>{q.scope}</span>{q.archived && <span className="ml-1 text-[10px] rounded-full px-1.5 py-0.5 bg-slate-100 text-slate-400">archived</span>}</p>
              <p className="text-xs text-slate-500 mt-0.5">{q.body}</p>
            </div>
            {q.can_manage && <button onClick={() => patch(q.id, { archived: !q.archived })} data-testid={`qr-archive-${q.id}`} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"><Archive className="w-3.5 h-3.5" />{q.archived ? "Restore" : "Archive"}</button>}
          </div>
        ))}
        {rows.length === 0 && <div className="px-4 py-8 text-center text-slate-400 text-sm">No Quick Replies yet.</div>}
      </div>
    </div>
  );
}
